/**
 * Retail Program create WIZARD.
 *   Step 1 — pick a program type (big buttons, like the calendar event wizard).
 *   Step 2 — details: CVS carrier/store + requested slots, OR a managed
 *            Table Top / Marketing Site location + available windows.
 *   Step 3 — assign the agent and submit.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, Store, CalendarClock, MapPin, Info,
  Building2, LayoutGrid, Megaphone, ArrowLeft, ArrowRight, User, Image as ImageIcon, CheckCircle2,
} from 'lucide-react';
import { retailProgramAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isAgent } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { flashAutofilled } from '@/lib/flashAutofilled';
import { TimeSelect, to12h } from '@/components/common/TimeSelect';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';

const CARRIERS = [
  { value: 'devoted', label: 'Devoted' },
  { value: 'solis', label: 'Solis' },
  { value: 'elevance', label: 'Elevance' },
];
const PROGRAM_CARDS = [
  { key: 'cvs', label: 'CVS (carrier)', icon: Building2,
    useFor: 'Carrier-sponsored CVS store assignment (Devoted, Solis, Elevance). Enter the store and request date/time slots.' },
  { key: 'table_top', label: 'Table Top', icon: LayoutGrid,
    useFor: 'A permitted table-top spot at a managed venue. Pick the location and its open windows.' },
  { key: 'marketing_site', label: 'Marketing Site', icon: Megaphone,
    useFor: 'A KR kiosk / sales post at a managed location. Pick the location and its open windows.' },
];
const SPONSOR_LABELS = {
  kr: 'KR (direct)', devoted: 'Devoted', solis: 'Solis', elevance: 'Elevance',
};
const STEP_LABELS = ['Type', 'Details', 'Agent'];
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
};

// Group open windows by calendar day and sort both the days and the
// time slots within each day, so the picker reads like a tidy calendar
// instead of a scattered chip cloud.
const groupWindowsByDate = (windows) => {
  const byDate = new Map();
  for (const w of windows || []) {
    if (!byDate.has(w.date)) byDate.set(w.date, []);
    byDate.get(w.date).push(w);
  }
  return Array.from(byDate.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, items]) => ({
      date,
      items: items.slice().sort((x, y) => (x.start || '').localeCompare(y.start || '')),
    }));
};

function emptySlot() {
  return { requested_date: '', requested_start: '10:00', requested_end: '14:00' };
}

export default function RetailRequestFormDialog({ open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const callerIsAgent = isAgent(user);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(null);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);

  // Location-program state.
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [windowIds, setWindowIds] = useState(new Set());
  const [venueStage, setVenueStage] = useState('location'); // 'location' | 'windows'

  const initialForm = useCallback(() => ({
    program_type: null,
    carrier: 'devoted',
    retailer: 'CVS',
    store_address: '', city: '', state: 'FL', zip_code: '',
    location_id: '',
    assigned_agent_npn: '',
    slots: [emptySlot()],
  }), []);

  useEffect(() => {
    if (!open) {
      setForm(null); setStep(1); setLocations([]);
      setSelectedLoc(null); setWindowIds(new Set()); setVenueStage('location');
      return;
    }
    setForm(initialForm());
    setStep(1);
    retailProgramAPI.agentOptions()
      .then((r) => setAgents(r.data.items || []))
      .catch(() => setAgents([]));
  }, [open, initialForm]);

  // Load locations when a location program is chosen.
  useEffect(() => {
    if (!form || !form.program_type || form.program_type === 'cvs') return;
    setLocLoading(true);
    setSelectedLoc(null);
    setWindowIds(new Set());
    setVenueStage('location');
    setForm((p) => ({ ...p, location_id: '' }));
    retailProgramAPI.listLocations({ classification: form.program_type, active: true })
      .then((r) => setLocations(r.data.items || []))
      .catch(() => setLocations([]))
      .finally(() => setLocLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.program_type]);

  const agentOptions = useMemo(() => agents, [agents]);
  const canEditAgent = !callerIsAgent;

  const setField = (patch) => setForm((p) => ({ ...p, ...patch }));
  const setSlot = (idx, patch) => setForm((p) => ({
    ...p, slots: p.slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
  }));
  const addSlot = () => setForm((p) => ({ ...p, slots: [...p.slots, emptySlot()] }));
  const removeSlot = (idx) => setForm((p) => ({
    ...p, slots: p.slots.length === 1 ? p.slots : p.slots.filter((_, i) => i !== idx),
  }));

  const pickType = (key) => { setField({ program_type: key }); setStep(2); };

  const pickLocation = async (locationId) => {
    setField({ location_id: locationId });
    setWindowIds(new Set());
    setSelectedLoc(null);
    if (!locationId) return;
    try {
      const r = await retailProgramAPI.getLocation(locationId);
      setSelectedLoc(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load location availability');
    }
  };

  const toggleWindow = (wid) => setWindowIds((prev) => {
    const next = new Set(prev);
    if (next.has(wid)) next.delete(wid); else next.add(wid);
    return next;
  });

  // Click a location card → load its availability and advance to the
  // windows picker (keeps the wizard feel the user asked for).
  const selectLocationCard = async (locationId) => {
    await pickLocation(locationId);
    setVenueStage('windows');
  };

  const validateDetails = () => {
    if (form.program_type === 'cvs') {
      if (!form.carrier) return 'Pick a carrier.';
      if (!form.store_address.trim()) return 'Store address is required.';
      if (!form.city.trim()) return 'City is required.';
      if (!/^\d{5}(-\d{4})?$/.test(form.zip_code.trim())) return 'ZIP code looks off (5 digits).';
      if (!form.slots.length) return 'Add at least one requested slot.';
      for (const s of form.slots) {
        if (!s.requested_date) return 'Each slot needs a requested date.';
        if (!s.requested_start || !s.requested_end) return 'Each slot needs start and end times.';
        if (s.requested_end <= s.requested_start) return 'End time must be after start time.';
      }
    } else {
      if (!form.location_id) return 'Pick a location.';
      if (windowIds.size === 0) return 'Select at least one available time window.';
    }
    return null;
  };

  const goNext = () => {
    if (step === 2) {
      const err = validateDetails();
      if (err) { toast.error(err); return; }
      setStep(3);
    }
  };

  const submit = async () => {
    const detailsErr = validateDetails();
    if (detailsErr) { toast.error(detailsErr); setStep(2); return; }
    if (canEditAgent && !form.assigned_agent_npn) { toast.error('Assign one agent.'); return; }
    setSaving(true);
    try {
      let payload;
      if (form.program_type === 'cvs') {
        payload = {
          program_type: 'cvs',
          carrier: form.carrier,
          retailer: form.retailer || 'CVS',
          store_address: form.store_address.trim(),
          city: form.city.trim(),
          state: (form.state || 'FL').toUpperCase().slice(0, 2),
          zip_code: form.zip_code.trim(),
          slots: form.slots.map((s) => ({
            requested_date: s.requested_date,
            requested_start: s.requested_start,
            requested_end: s.requested_end,
          })),
        };
      } else {
        const windows = (selectedLoc?.available_windows || []).filter((w) => windowIds.has(w.window_id));
        payload = {
          program_type: form.program_type,
          location_id: form.location_id,
          slots: windows.map((w) => ({
            window_id: w.window_id,
            requested_date: w.date,
            requested_start: w.start,
            requested_end: w.end,
          })),
        };
      }
      if (canEditAgent) payload.assigned_agent_npn = form.assigned_agent_npn;
      await retailProgramAPI.create(payload);
      toast.success('Retail Program request submitted');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;
  const sponsor = selectedLoc?.sponsor;
  const card = PROGRAM_CARDS.find((c) => c.key === form.program_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="retail-form-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Store size={18} className="text-emerald-600" /> New Retail Program Request
          </DialogTitle>
          <DialogDescription>
            {step === 1 ? 'Choose the type of request to get started.'
              : step === 2 ? `${card?.label} — add the details.`
                : 'Assign the agent and submit.'}
          </DialogDescription>
          {/* Stepper */}
          <div className="flex items-center gap-2 pt-2" data-testid="retail-wizard-steps">
            {STEP_LABELS.map((lbl, i) => {
              const n = i + 1;
              const done = step > n;
              const active = step === n;
              return (
                <div key={lbl} className="flex items-center gap-2">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${active ? 'bg-emerald-600 text-white' : done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                    {n}
                  </span>
                  <span className={`text-xs ${active ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{lbl}</span>
                  {n < STEP_LABELS.length && <span className="w-5 h-px bg-slate-200" />}
                </div>
              );
            })}
          </div>
        </DialogHeader>

        <div className="px-6 pb-2 space-y-4">
          {/* STEP 1 — type buttons */}
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1" data-testid="retail-wizard-type-cards">
              {PROGRAM_CARDS.map((c) => {
                const Icon = c.icon;
                return (
                  <button key={c.key} type="button" onClick={() => pickType(c.key)}
                    data-testid={`retail-program-card-${c.key}`}
                    className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-emerald-400 hover:shadow-md">
                    <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100">
                      <Icon size={18} />
                    </span>
                    <span className="text-sm font-semibold text-slate-900">{c.label}</span>
                    <span className="text-xs text-slate-500 leading-snug">{c.useFor}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* STEP 2 — details */}
          {step === 2 && form.program_type === 'cvs' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Carrier *</Label>
                  <Select value={form.carrier} onValueChange={(v) => setField({ carrier: v })}>
                    <SelectTrigger data-testid="retail-form-carrier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CARRIERS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Retailer</Label>
                  <Input value={form.retailer} disabled data-testid="retail-form-retailer" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Store address *</Label>
                  <AddressAutocomplete
                    value={form.store_address}
                    onChange={(v) => setField({ store_address: v })}
                    onPlaceSelected={(parsed) => {
                      setField({
                        store_address: parsed.street || parsed.formatted,
                        city: parsed.city || form.city,
                        state: parsed.state || form.state,
                        zip_code: parsed.zip || form.zip_code,
                      });
                      flashAutofilled(['retail-form-city', 'retail-form-state', 'retail-form-zip']);
                    }}
                    placeholder="Start typing the CVS store address…"
                    testId="retail-form-address"
                  />
                </div>
                <div>
                  <Label className="text-xs">City *</Label>
                  <Input value={form.city} onChange={(e) => setField({ city: e.target.value })} data-testid="retail-form-city" />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={form.state} maxLength={2}
                    onChange={(e) => setField({ state: e.target.value.toUpperCase().slice(0, 2) })}
                    data-testid="retail-form-state" />
                </div>
                <div>
                  <Label className="text-xs">ZIP *</Label>
                  <Input value={form.zip_code} onChange={(e) => setField({ zip_code: e.target.value })}
                    placeholder="32801" data-testid="retail-form-zip" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs flex items-center gap-1">
                    <CalendarClock size={12} className="text-purple-500" /> Requested date/time slots *
                  </Label>
                  <Button variant="outline" size="sm" onClick={addSlot} data-testid="retail-form-add-slot">
                    <Plus size={12} className="mr-1" /> Add slot
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.slots.map((s, i) => (
                    <div key={i}
                      className="grid grid-cols-[1.2fr_1fr_1fr_auto] gap-2 items-end p-2 border border-slate-200 rounded"
                      data-testid={`retail-form-slot-${i}`}>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
                        <Input type="date" value={s.requested_date}
                          onChange={(e) => setSlot(i, { requested_date: e.target.value })}
                          data-testid={`retail-form-slot-date-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Start</Label>
                        <TimeSelect value={s.requested_start}
                          onChange={(v) => setSlot(i, { requested_start: v })}
                          testId={`retail-form-slot-start-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">End</Label>
                        <TimeSelect value={s.requested_end}
                          onChange={(v) => setSlot(i, { requested_end: v })}
                          testId={`retail-form-slot-end-${i}`} />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeSlot(i)}
                        disabled={form.slots.length === 1} data-testid={`retail-form-slot-remove-${i}`}>
                        <Trash2 size={13} className={form.slots.length === 1 ? 'text-slate-300' : 'text-rose-500'} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && form.program_type !== 'cvs' && (
            <>
              {venueStage === 'location' ? (
                <div>
                  <Label className="text-xs flex items-center gap-1 mb-2">
                    <MapPin size={12} className="text-emerald-600" /> Pick a location *
                  </Label>
                  {locLoading ? (
                    <div className="text-xs text-slate-500 py-2"><Loader2 size={13} className="inline mr-1 animate-spin" />Loading locations…</div>
                  ) : locations.length === 0 ? (
                    <p className="text-xs text-slate-500 italic mt-1" data-testid="retail-form-no-locations">
                      No active {form.program_type === 'table_top' ? 'Table Top' : 'Marketing Site'} locations yet.
                      A KR admin can add them from “Manage Locations”.
                    </p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="retail-form-location-cards">
                      {locations.map((l) => (
                        <LocationCard
                          key={l.location_id}
                          loc={l}
                          selected={form.location_id === l.location_id}
                          onSelect={() => selectLocationCard(l.location_id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setVenueStage('location')}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
                    data-testid="retail-form-change-location"
                  >
                    <ArrowLeft size={13} /> Choose a different location
                  </button>
                  {selectedLoc && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="retail-form-location-detail">
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <span className="font-medium text-slate-900">{selectedLoc.name}</span>
                    <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                      Sponsor: {SPONSOR_LABELS[sponsor] || sponsor}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {selectedLoc.address}, {selectedLoc.city}, {selectedLoc.state} {selectedLoc.zip_code}
                  </p>
                  <p className="text-[11px] text-slate-500 flex items-start gap-1">
                    <Info size={12} className="mt-0.5 shrink-0" />
                    {sponsor === 'kr'
                      ? 'KR-sponsored — KR reviews and approves directly (no carrier step).'
                      : `${SPONSOR_LABELS[sponsor] || sponsor}-sponsored — goes to KR review, then sent to the carrier.`}
                  </p>
                  <div>
                    <Label className="text-xs flex items-center gap-1 mb-1.5">
                      <CalendarClock size={12} className="text-purple-500" /> Pick available windows *
                    </Label>
                    {(selectedLoc.available_windows || []).length === 0 ? (
                      <p className="text-[11px] text-rose-500 italic" data-testid="retail-form-no-windows">
                        No open windows right now — all are booked. Try another location.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-auto pr-1" data-testid="retail-form-windows">
                        {groupWindowsByDate(selectedLoc.available_windows).map((grp) => (
                          <div key={grp.date} className="rounded-lg border border-slate-200 overflow-hidden">
                            <div className="flex items-center justify-between bg-slate-50 px-2.5 py-1.5 border-b border-slate-200">
                              <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
                                <CalendarClock size={11} className="text-purple-500" /> {fmtDate(grp.date)}
                              </span>
                              <span className="text-[10px] text-slate-400">{grp.items.length} slot{grp.items.length > 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 p-2">
                              {grp.items.map((w) => {
                                const on = windowIds.has(w.window_id);
                                return (
                                  <button key={w.window_id} type="button" onClick={() => toggleWindow(w.window_id)}
                                    data-testid={`retail-form-window-${w.window_id}`}
                                    className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'}`}>
                                    {to12h(w.start)}–{to12h(w.end)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {windowIds.size > 0 && (
                      <p className="text-[11px] text-emerald-600 mt-1.5">{windowIds.size} window(s) selected</p>
                    )}
                  </div>
                </div>
                  )}
                </>
              )}
            </>
          )}

          {/* STEP 3 — agent + review */}
          {step === 3 && (
            <>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <Label className="text-xs flex items-center gap-1"><User size={12} /> Assigned agent *</Label>
                {callerIsAgent ? (
                  <p className="text-sm text-slate-700 mt-1">
                    You will be assigned automatically — one request supports one agent.
                  </p>
                ) : agentOptions.length === 0 ? (
                  <p className="text-xs text-slate-500 italic mt-2">
                    No agents available. If you are an Agency Principal, ensure your downline is populated.
                  </p>
                ) : (
                  <Select value={form.assigned_agent_npn} onValueChange={(v) => setField({ assigned_agent_npn: v })}>
                    <SelectTrigger data-testid="retail-form-agent">
                      <SelectValue placeholder="Pick an agent from your downline" />
                    </SelectTrigger>
                    <SelectContent>
                      {agentOptions.map((a) => (
                        <SelectItem key={a.npn} value={a.npn}>{a.name} · NPN {a.npn}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1" data-testid="retail-form-review">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Review</p>
                <div className="flex justify-between"><span className="text-slate-500">Program</span><span className="font-medium">{card?.label}</span></div>
                {form.program_type === 'cvs' ? (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">Carrier</span><span className="font-medium">{CARRIERS.find((c) => c.value === form.carrier)?.label}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">Store</span><span className="font-medium text-right">{form.store_address}, {form.city}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Slots</span><span className="font-medium">{form.slots.length}</span></div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between gap-4"><span className="text-slate-500">Location</span><span className="font-medium text-right">{selectedLoc?.name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Sponsor</span><span className="font-medium">{SPONSOR_LABELS[sponsor] || sponsor}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Windows</span><span className="font-medium">{windowIds.size}</span></div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4 bg-slate-50">
          {step > 1 ? (
            <Button
              variant="ghost"
              onClick={() => {
                if (step === 2 && form.program_type !== 'cvs' && venueStage === 'windows') {
                  setVenueStage('location');
                } else {
                  setStep(step - 1);
                }
              }}
              disabled={saving}
              data-testid="retail-wizard-back"
            >
              <ArrowLeft size={15} className="mr-1" /> Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="retail-wizard-cancel">Cancel</Button>
          )}
          {step === 2 && !(form.program_type !== 'cvs' && venueStage === 'location') && (
            <Button onClick={goNext} data-testid="retail-wizard-next">
              Continue <ArrowRight size={15} className="ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button onClick={submit} disabled={saving} data-testid="retail-form-submit">
              {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
              Submit request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// A selectable location card (cover image + details). Clicking it picks
// the location and advances the wizard to the window picker.
function LocationCard({ loc, selected, onSelect }) {
  const coverUrl = useInternalCoverBlob(loc.cover_url || loc.photo_url || null);
  const soldOut = (loc.available_count || 0) === 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={soldOut}
      className={`text-left rounded-xl border overflow-hidden transition-all ${
        selected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-emerald-400 hover:shadow-md'
      } ${soldOut ? 'opacity-60 cursor-not-allowed' : ''}`}
      data-testid={`retail-form-location-card-${loc.location_id}`}
    >
      <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
        {coverUrl ? (
          <img src={coverUrl} alt={loc.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-slate-300 flex flex-col items-center gap-1 text-[11px]"><ImageIcon size={22} /> No photo</span>
        )}
      </div>
      <div className="p-2.5 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-sm text-slate-900 leading-tight">{loc.name}</span>
          {selected && <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />}
        </div>
        <p className="text-[11px] text-slate-500 flex items-center gap-1">
          <MapPin size={11} /> {loc.city}, {loc.state}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
          <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
            {SPONSOR_LABELS[loc.sponsor] || loc.sponsor}
          </Badge>
          <span className={`text-[10px] font-medium flex items-center gap-1 ${soldOut ? 'text-rose-500' : 'text-emerald-600'}`}>
            <CalendarClock size={11} /> {soldOut ? 'Fully booked' : `${loc.available_count} open`}
          </span>
        </div>
      </div>
    </button>
  );
}
